/**
 * The celebration on the accepted 5th repetition (R13; Sobhan, Telegram
 * 2026-10-06: a confetti burst like his reference short, played with the
 * `rep_final_ok` cue). Drawn in code — no library, no video: a few dozen
 * paper pieces in the app's brand colours shoot up from below the middle,
 * spread, flutter and fall, and are gone in under two seconds.
 *
 * The physics is a pure function of time, so a frame can be computed (and
 * tested) without a canvas or a clock: `confettiPieces` makes the burst,
 * `pieceAt` says where one piece is `t` ms in.
 */

export const CONFETTI_MS = 1800
export const CONFETTI_COUNT = 54

/** Brand blue (two shades), good green, warn orange, bad red and accent yellow — the palette of the app itself. */
export const CONFETTI_COLORS = ['#2b5a96', '#4a72a8', '#2e9e6b', '#e08a2b', '#d2523f', '#f2be1a']

/** px/s² in CSS pixels; strong enough that the pieces come back down within the burst. */
const GRAVITY = 1400
/** Air: horizontal speed lost per second, as a fraction. */
const DRAG = 1.6

/** A small deterministic generator, so a burst is reproducible in tests and captures. */
export function seededRandom(seed = 1) {
  let state = seed >>> 0 || 1
  return () => {
    state ^= state << 13
    state ^= state >>> 17
    state ^= state << 5
    return ((state >>> 0) % 100000) / 100000
  }
}

/** One burst for a stage of `width` × `height` CSS pixels. */
export function confettiPieces(width, height, { count = CONFETTI_COUNT, random = Math.random } = {}) {
  const originX = width / 2
  const originY = height * 0.62
  return Array.from({ length: count }, (_, index) => {
    // A fan upwards: −150°…−30°, faster towards the middle.
    const angle = (-150 + random() * 120) * (Math.PI / 180)
    const speed = (0.55 + random() * 0.45) * Math.max(height, 480) * 1.45
    return {
      x0: originX + (random() - 0.5) * width * 0.12,
      y0: originY,
      vx: Math.cos(angle) * speed * (width / Math.max(width, height)) * 1.25,
      vy: Math.sin(angle) * speed,
      w: 6 + random() * 5,
      h: 9 + random() * 7,
      rotation: random() * Math.PI * 2,
      spin: (random() - 0.5) * 14,
      flutter: 2 + random() * 3,
      phase: random() * Math.PI * 2,
      color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
    }
  })
}

/**
 * Where a piece is `t` ms into the burst: thrown, slowed by the air,
 * pulled down, turning, its width breathing as it flips (the flutter), and
 * fading over the last third.
 */
export function pieceAt(piece, t) {
  const s = Math.max(0, t) / 1000
  const slowed = (1 - Math.exp(-DRAG * s)) / DRAG
  const x = piece.x0 + piece.vx * slowed + Math.sin(piece.phase + s * piece.flutter * 2) * 14 * s
  const y = piece.y0 + piece.vy * s + 0.5 * GRAVITY * s * s
  const fadeFrom = CONFETTI_MS * 0.66
  const alpha = t <= fadeFrom ? 1 : Math.max(0, 1 - (t - fadeFrom) / (CONFETTI_MS - fadeFrom))
  return {
    x,
    y,
    rotation: piece.rotation + piece.spin * s,
    scaleX: Math.cos(piece.phase + s * piece.flutter * 3),
    alpha,
  }
}

/** Draws the burst `t` ms in onto a 2D context of `width` × `height` CSS pixels. */
export function drawConfetti(context, pieces, t, width, height) {
  context.clearRect(0, 0, width, height)
  for (const piece of pieces) {
    const at = pieceAt(piece, t)
    if (at.alpha <= 0 || at.y > height + 40) continue
    context.save()
    context.globalAlpha = at.alpha
    context.translate(at.x, at.y)
    context.rotate(at.rotation)
    context.scale(at.scaleX, 1)
    context.fillStyle = piece.color
    context.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h)
    context.restore()
  }
}
